import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Send, Search, PenSquare, ArrowLeft, Check, CheckCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateGroup(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function groupByDate(messages) {
  const groups = [];
  let currentDate = '';
  for (const m of messages) {
    const dateLabel = formatDateGroup(m.sent_at);
    if (dateLabel !== currentDate) {
      currentDate = dateLabel;
      groups.push({ type: 'date', label: dateLabel });
    }
    groups.push({ type: 'message', data: m });
  }
  return groups;
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activePartner, setActivePartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [showList, setShowList] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [typing, setTyping] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const chatContainerRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeout = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try { const { data } = await api.get('/messages/conversations'); setConversations(data); } catch {}
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token') || document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
    if (!token) return;
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/messages/ws?token=${token}`;
    try {
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
          fetchConversations();
        }
        if (data.type === 'typing' && data.from === activePartner?.partner_id) {
          setTyping(true);
          clearTimeout(typingTimeout.current);
          typingTimeout.current = setTimeout(() => setTyping(false), 3000);
        }
      };
      wsRef.current = ws;
      return () => ws.close();
    } catch {}
  }, [activePartner?.partner_id, fetchConversations]);

  // Fetch messages
  const fetchMessages = useCallback(async (partnerId, skip = 0) => {
    try {
      const { data } = await api.get(`/messages/${partnerId}?skip=${skip}&limit=50`);
      if (skip === 0) setMessages(data.messages);
      else setMessages(prev => [...data.messages, ...prev]);
      setHasMore(data.has_more);
    } catch {}
  }, []);

  useEffect(() => {
    if (activePartner) {
      fetchMessages(activePartner.partner_id);
      const interval = setInterval(() => fetchMessages(activePartner.partner_id), 5000);
      return () => clearInterval(interval);
    }
  }, [activePartner, fetchMessages]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load more on scroll up
  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMore && !loadingMore && activePartner) {
      setLoadingMore(true);
      fetchMessages(activePartner.partner_id, messages.length).finally(() => setLoadingMore(false));
    }
  };

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === 1 && activePartner) {
      wsRef.current.send(JSON.stringify({ type: 'typing', to: activePartner.partner_id }));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activePartner) return;
    try {
      const { data } = await api.post('/messages', { receiver_id: activePartner.partner_id, content: newMsg.trim() });
      setMessages(prev => [...prev, data]);
      setNewMsg('');
      fetchConversations();
    } catch {}
  };

  const openConvo = (partner) => {
    setActivePartner(partner);
    setShowList(false);
    setTyping(false);
  };

  const startNewConvo = async () => {
    try { const { data } = await api.get('/messages/contactable-users'); setContacts(data); setShowNewConvo(true); } catch {}
  };

  const selectContact = (contact) => {
    setShowNewConvo(false);
    const existing = conversations.find(c => c.partner_id === contact._id);
    if (existing) { openConvo(existing); return; }
    openConvo({ partner_id: contact._id, partner_name: contact.name, partner_role: contact.role, unread: 0 });
  };

  // Block file paste/drop
  const handlePaste = (e) => { if (e.clipboardData?.files?.length) e.preventDefault(); };
  const handleDrop = (e) => { e.preventDefault(); };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); }
    handleTyping();
  };

  const filteredConvos = conversations.filter(c => !search || c.partner_name.toLowerCase().includes(search.toLowerCase()));
  const filteredContacts = contacts.filter(c => !contactSearch || c.name.toLowerCase().includes(contactSearch.toLowerCase()));
  const grouped = groupByDate(messages);
  const roleColor = { CLIENT: 'bg-[#0D8B7D]/10 text-[#0D8B7D]', THERAPIST: 'bg-[#F5B041]/10 text-[#F5B041]', ADMIN: 'bg-[#4A6B68]/10 text-[#4A6B68]' };

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white rounded-2xl border border-[#E5F2F1] overflow-hidden" data-testid="messages-page" onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
      {/* Conversation List */}
      <div className={`${showList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-80 border-r border-[#E5F2F1]`}>
        <div className="p-3 border-b border-[#E5F2F1] space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#1A3330] font-['Outfit']">Messages</h3>
            <Button variant="ghost" size="sm" onClick={startNewConvo} className="h-8 w-8 p-0" data-testid="new-convo-btn"><PenSquare className="w-4 h-4 text-[#0D8B7D]" /></Button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A6B68]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-8 rounded-xl border-[#E5F2F1] text-xs" data-testid="convo-search" />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {filteredConvos.length === 0 && <p className="p-4 text-sm text-[#4A6B68] text-center">{conversations.length === 0 ? 'No messages yet. Start a conversation!' : 'No matching conversations'}</p>}
          {filteredConvos.map(c => (
            <button key={c.partner_id} onClick={() => openConvo(c)} data-testid={`convo-${c.partner_id}`}
              className={`w-full text-left px-3 py-3 border-b border-[#E5F2F1] hover:bg-[#F4FAF9] transition-colors ${activePartner?.partner_id === c.partner_id ? 'bg-[#0D8B7D]/5' : ''}`}>
              <div className="flex items-center gap-2.5">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-[#0D8B7D]/10 flex items-center justify-center text-[#0D8B7D] text-xs font-semibold">
                    {c.partner_name?.split(' ').map(n => n[0]).join('')}
                  </div>
                  {c.online && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#2E8B57] border-2 border-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-[#1A3330] truncate">{c.partner_name}</p>
                      <Badge className={`${roleColor[c.partner_role] || ''} text-[8px] border-0 px-1 py-0`}>{c.partner_role}</Badge>
                    </div>
                    <span className="text-[9px] text-[#4A6B68] flex-shrink-0">{c.last_time ? formatTime(c.last_time) : ''}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#4A6B68] truncate">{c.last_message}</p>
                    {c.unread > 0 && <Badge className="bg-[#3B82F6] text-white text-[9px] px-1.5 py-0 ml-1 flex-shrink-0">{c.unread}</Badge>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Chat Window */}
      <div className={`${!showList || activePartner ? 'flex' : 'hidden'} md:flex flex-1 flex-col`}>
        {activePartner ? (
          <>
            <div className="p-3 border-b border-[#E5F2F1] flex items-center gap-3">
              <button onClick={() => setShowList(true)} className="md:hidden text-[#4A6B68]" data-testid="back-to-convos"><ArrowLeft className="w-5 h-5" /></button>
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-[#0D8B7D]/10 flex items-center justify-center text-[#0D8B7D] text-xs font-semibold">
                  {activePartner.partner_name?.split(' ').map(n => n[0]).join('')}
                </div>
                {activePartner.online && <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#2E8B57] border-2 border-white" />}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-[#1A3330]">{activePartner.partner_name}</p>
                  <Badge className={`${roleColor[activePartner.partner_role] || ''} text-[8px] border-0 px-1 py-0`}>{activePartner.partner_role}</Badge>
                </div>
                {typing ? (
                  <p className="text-[10px] text-[#0D8B7D] animate-pulse">typing...</p>
                ) : (
                  <p className="text-[10px] text-[#4A6B68]">{activePartner.online ? 'Online' : 'Offline'}</p>
                )}
              </div>
            </div>
            <div ref={chatContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4" data-testid="chat-area">
              {loadingMore && <div className="text-center text-xs text-[#4A6B68] py-2">Loading older messages...</div>}
              <div className="space-y-1">
                {grouped.map((item, i) => {
                  if (item.type === 'date') return (
                    <div key={`d-${i}`} className="flex justify-center py-3"><span className="text-[10px] text-[#4A6B68] bg-[#F4FAF9] px-3 py-1 rounded-full">{item.label}</span></div>
                  );
                  const m = item.data;
                  const isMine = m.sender_id === user._id;
                  return (
                    <div key={m._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${isMine ? 'bg-[#0D8B7D] text-white rounded-br-sm' : 'bg-[#F4FAF9] text-[#1A3330] rounded-bl-sm'}`}>
                        <p className="whitespace-pre-wrap break-words">{m.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-white/60' : 'text-[#4A6B68]'}`}>
                          <span className="text-[9px]">{formatTime(m.sent_at)}</span>
                          {isMine && (m.is_read ? <CheckCheck className="w-3 h-3 text-[#88D4CC]" /> : <Check className="w-3 h-3" />)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={scrollRef} />
              </div>
            </div>
            <form onSubmit={sendMessage} className="p-3 border-t border-[#E5F2F1] flex gap-2">
              <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste}
                placeholder="Type a message..." rows={1}
                className="flex-1 rounded-xl border border-[#E5F2F1] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#0D8B7D]"
                data-testid="message-input" />
              <Button type="submit" disabled={!newMsg.trim()} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl self-end" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#4A6B68] text-sm" data-testid="no-convo-selected">
            Select a conversation from the left or start a new one
          </div>
        )}
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConvo} onOpenChange={setShowNewConvo}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="new-convo-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">New Message</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A6B68]" />
              <Input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search users..." className="pl-9 rounded-xl border-[#E5F2F1]" data-testid="contact-search" />
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {filteredContacts.map(c => (
                <button key={c._id} onClick={() => selectContact(c)} className="w-full text-left px-3 py-2.5 hover:bg-[#F4FAF9] rounded-xl flex items-center gap-3 transition-colors" data-testid={`contact-${c._id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#0D8B7D]/10 flex items-center justify-center text-[#0D8B7D] text-xs font-semibold">{c.name?.split(' ').map(n => n[0]).join('')}</div>
                  <div>
                    <p className="text-sm font-medium text-[#1A3330]">{c.name}</p>
                    <Badge className={`${roleColor[c.role] || ''} text-[8px] border-0 px-1 py-0`}>{c.role}</Badge>
                  </div>
                </button>
              ))}
              {filteredContacts.length === 0 && <p className="text-center text-sm text-[#4A6B68] py-4">No users available</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
